import { HttpClient } from '@angular/common/http';
import { Injectable, Optional } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IMemberProfilePaymentCard } from '@shared/shared/interfaces/member-profile-payment-card.interfaces';
import { IMemberProfile } from '@shared/shared/interfaces/member-profile.interfaces';
import { replaceTemplateKeys } from '@shared/utils/url.lib';
import { CompleteMemberProfilePopupComponent } from 'core/components/complete-member-profile-popup/complete-member-profile-popup.component';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as MemberProfileActions from '../actions/member-profile.actions';
import { MemberProfileState } from '../reducers';

@Injectable()
export class MemberProfileEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private router: Router,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef: MatDialogRef<CompleteMemberProfilePopupComponent>,
    private ngrxStore: Store<{ 'member-profile': MemberProfileState }>
  ) {}

  public viewProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileActions.VIEW_PROFILE),
      switchMap((action: MemberProfileActions.ViewProfile) => {
        const memberProfileUrl = replaceTemplateKeys(ApiEndpoint.MEMBER_PROFILES, { id: action.payload });
        const paymentCardsUrl = ApiEndpoint.MEMBER_PROFILE_PAYMENT_CARD;
        const paymentCards$ = this.http
          .get<IMemberProfilePaymentCard[]>(paymentCardsUrl, {
            params: { online_use: 'true', offline_use: 'false' },
          })
          .pipe(catchError(() => of([])));

        return forkJoin([this.http.get<IMemberProfile>(memberProfileUrl), paymentCards$]).pipe(
          map(([profile, payment_cards]) => new MemberProfileActions.ViewProfileSuccess({ ...profile, payment_cards })),
          catchError((error) => of(new MemberProfileActions.ViewProfileFail(error)))
        );
      })
    )
  );

  public updateEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileActions.UPDATE_EMAIL),
      withLatestFrom(this.ngrxStore),
      switchMap(([action, state]: [MemberProfileActions.UpdateEmail, { 'member-profile': MemberProfileState }]) => {
        const id = state['member-profile'].memberProfile.id.toString();
        const url = replaceTemplateKeys(ApiEndpoint.MEMBER_PROFILE_EMAIL, { id });

        return this.http.post(url, action.payload).pipe(
          map((email) => new MemberProfileActions.UpdateEmailSuccess(email)),
          catchError(() => of(new MemberProfileActions.UpdateEmailFail()))
        );
      })
    )
  );

  public updateShowTour$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileActions.UPDATE_SHOW_TOUR),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state]) => {
        const id = state['member-profile'].memberProfile.id.toString();
        const url = replaceTemplateKeys(ApiEndpoint.MEMBER_PROFILES, { id });

        return this.http.patch(url, { show_tour: false }).pipe(
          map(() => new MemberProfileActions.UpdateShowTourSuccess(false)),
          catchError(() =>
            of(new MemberProfileActions.UpdateShowTourFail({ value: 'could not update member profile' }))
          )
        );
      })
    )
  );

  public updateMemberProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberProfileActions.UPDATE_MEMBER_PROFILE),
      withLatestFrom(this.ngrxStore),
      switchMap(
        ([action, state]: [MemberProfileActions.UpdateMemberProfile, { 'member-profile': MemberProfileState }]) => {
          const id = state['member-profile'].memberProfile.id.toString();
          const url = replaceTemplateKeys(ApiEndpoint.MEMBER_PROFILES, { id });

          return this.http.patch(url, action.payload).pipe(
            map((res) => new MemberProfileActions.UpdateMemberProfileSuccess(res)),
            catchError((err) => {
              if (err?.status === 429) {
                this.snackBar.open('Error occurred', 'Close', { duration: 1000 });
                this.dialogRef?.close();
                this.router.navigate(['/error/rate-limitation']);
              } else {
                return of(
                  new MemberProfileActions.UpdateMemberProfileFail(
                    err?.error || { value: 'could not update member profile' }
                  )
                );
              }
            })
          );
        }
      )
    )
  );
}
